import React, { useEffect, useRef, useState } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { useRemScale } from 'lib/remScale';
import { TYPE, FONTS } from 'style/typography';

const NEXT_AFTER_MS = 1800;

/**
 * The guided first visit's quiz: the course's two short questions. Choosing an answer shows at once whether it was
 * right, and the right one if not, with a one-line why; the next question follows by itself. Finishing earns Quiz
 * Starter whatever the answers (Sol badge-contract Q1) - the real quizzes still decide Flawless First.
 */
export default function TourQuizPanel({ entry, onDone }) {
  const { rem } = useRemScale();
  const [position, setPosition] = useState(0);
  const [chosen, setChosen] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    setPosition(0);
    setChosen(null);
  }, [entry]);

  const questions = entry?.quiz || [];
  const question = questions[position];
  if (!question) return null;

  const choose = option => {
    if (chosen) return;
    setChosen(option);
    timer.current = setTimeout(() => {
      if (position + 1 < questions.length) {
        setPosition(position + 1);
        setChosen(null);
      } else if (onDone) {
        onDone();
      }
    }, NEXT_AFTER_MS);
  };

  return (
    <View style={styles.overlay} testID="first-visit-quiz">
      <View style={[styles.card, { borderRadius: rem(20), padding: rem(20), width: rem(640), maxWidth: '92%' }]}>
        <Text style={[styles.count, { fontSize: rem(TYPE.sm), fontFamily: FONTS.body }]}>
          {`Question ${position + 1} of ${questions.length}`}
        </Text>
        {question.image ? (
          <Image
            source={{ uri: question.image }}
            style={[styles.picture, { borderRadius: rem(12), height: rem(200), marginTop: rem(10) }]}
            resizeMode="cover"
          />
        ) : null}
        <Text style={[styles.question, { fontSize: rem(TYPE.xl), fontFamily: FONTS.body, marginTop: rem(14) }]}>
          {question.question}
        </Text>
        <View style={{ marginTop: rem(12) }}>
          {question.options.map((option, i) => {
            const isAnswer = option === question.answer;
            const state = !chosen ? 'idle' : isAnswer ? 'right' : option === chosen ? 'wrong' : 'idle';
            return (
              <TouchableOpacity
                key={option}
                testID={`first-visit-quiz-option-${i}`}
                activeOpacity={0.85}
                disabled={!!chosen}
                onPress={() => choose(option)}
                style={[
                  styles.option,
                  { borderRadius: rem(12), paddingVertical: rem(12), paddingHorizontal: rem(14), marginTop: rem(8) },
                  state === 'right' && styles.right,
                  state === 'wrong' && styles.wrong,
                ]}>
                <Text style={[styles.optionText, { fontSize: rem(TYPE.base), fontFamily: FONTS.body }]}>{option}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
        {chosen ? (
          <Text style={[styles.why, { fontSize: rem(TYPE.base), fontFamily: FONTS.body, marginTop: rem(12) }]}>
            {`${chosen === question.answer ? 'Correct! ' : 'Not quite. '}${question.explanation || ''}`}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 92,
  },
  card: { backgroundColor: 'rgba(15,16,20,0.96)', borderWidth: 1, borderColor: 'rgba(197,232,54,0.30)' },
  count: { color: '#C5E836', fontWeight: '700', letterSpacing: 0.4 },
  picture: { width: '100%', backgroundColor: 'rgba(255,255,255,0.05)' },
  question: { color: '#F6FBFF', fontWeight: '700' },
  option: { borderWidth: 2, borderColor: 'rgba(255,255,255,0.14)', backgroundColor: 'rgba(255,255,255,0.04)' },
  right: { borderColor: '#C5E836', backgroundColor: 'rgba(197,232,54,0.14)' },
  wrong: { borderColor: '#F26D6D', backgroundColor: 'rgba(242,109,109,0.12)' },
  optionText: { color: '#F6FBFF', fontWeight: '600' },
  why: { color: 'rgba(246,251,255,0.82)' },
});
